import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import Layout from '../../components/layout/Layout';
import { apiClient } from '../../services';
import { LectureFaqResponse, LectureFaqItem, RecommenderResponse, RecommendeeItem } from '../../types/lecture';
import LoginModal from '../../components/modals/LoginModal';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { logout } from '../../store/slices/authSlice';
import { pickThumbnail } from '../../utils/thumbnail';

const ClassLecture: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, token } = useSelector((state: RootState) => state.auth);
  
  const [lectureData, setLectureData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab, setActiveTab] = useState('introduction');
  const [isTabSticky, setIsTabSticky] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [isTabClicked, setIsTabClicked] = useState(false); // 탭 클릭 상태 추가
  const [targetScrollPosition, setTargetScrollPosition] = useState<number | null>(null); // 목표 스크롤 위치
  
  // 이미지 로딩 상태 관리
  const [bannerImageLoaded, setBannerImageLoaded] = useState(false);
  // FAQ 데이터 상태
  const [lectureFaqs, setLectureFaqs] = useState<LectureFaqItem[] | null>(null);
  const [faqError, setFaqError] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  // 추천 강의 상태
  const [recommended, setRecommended] = useState<RecommendeeItem[] | null>(null);
  const [recommendedError, setRecommendedError] = useState(false);
  const [recommendedTitle, setRecommendedTitle] = useState<string>('추천 강의');

  // 탭 데이터 - API 응답에 따라 동적으로 생성
  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'introduction', label: '강의 소개', condition: () => lectureData?.introduction_url },
      { id: 'instructor', label: '교수 소개', condition: () => lectureData?.lecturer_introduction_url },
      { id: 'reviews', label: '수강 후기', condition: () => lectureData?.lecture_review_url },
      { id: 'recommended', label: '추천 강의', condition: () => recommended && recommended.length > 0 },
      { id: 'faq', label: 'FAQ', condition: () => true }, // 항상 표시
    ];
    
    // 조건을 만족하는 탭만 필터링
    return baseTabs.filter(tab => tab.condition());
  }, [lectureData, recommended]);

  // 탭 목록이 변경될 때 activeTab이 유효한지 확인하고 필요시 첫 번째 탭으로 설정
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(tab => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);
  
  // 탭과 섹션 참조
  const tabSectionRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // 탭 섹션의 원래 위치를 저장 (변하지 않는 값)
  const originalTabPositionRef = useRef<number>(0);

  // 페이지 로드 시 스크롤 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 강의 데이터 가져오기
  useEffect(() => {
    const fetchLectureData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        setBannerImageLoaded(false); // 새 강의 데이터 로딩 시 이미지 로딩 상태 초기화
        
        // 강의 상세 정보 API 사용
        /// console.log('요청:', `/api/lectures/detail/${id}`);
        
        try {
          const { data } = await apiClient.get<{
            code: number; status: string; message: string; data: any
          }>(`/api/lectures/detail/${id}`);
          /// console.log('강의 상세 정보 API 응답:', {data});
          // /// console.log('강의 상세 데이터 필드들:', Object.keys(data.data || {}));
          
          if (data.code === 200) {
            // /// console.log('강의 배너 URL:', data.data.banner_url);
            // /// console.log('강의 Vimeo ID:', data.data.vimeo_id);
            // /// console.log('강의 소개 URL:', data.data.introduction_url);
            // /// console.log('교수 소개 URL:', data.data.lecturer_introduction_url);
            // /// console.log('수강후기 URL:', data.data.lecture_review_url);
            /// console.log('교수 소속/직함:', data.data.lecturer_department);
            setLectureData(data.data);
          } else {
            /// console.log('응답 코드 오류(200이아님), 기본 데이터 사용');
            setLectureData({
              id: id,
              title: `강의 ${id}`,
              lecturer_name: '교수명',
              lecturer_department: '소속/직함',
              banner_url: null,
              vimeo_id: '1093667661'
            });
          }
        } catch (err: any) {
          /// console.log('강의 상세 API 실패, 기본 데이터 사용');
          // 404여도 기본 데이터로 계속 진행
          setLectureData({
            id: id,
            title: `강의 ${id}`,
            lecturer_name: '교수명',
            lecturer_department: '소속/직함',
            banner_url: null,
            vimeo_id: '1093667661'
          });
          return;
        }
      } catch (error) {
        /// console.error('강의 데이터 가져오기 오류:', error);
        // 에러 발생 시에도 기본 데이터로 진행
        setLectureData({
          id: id,
          title: `강의 ${id}`,
          lecturer_name: '교수명',
          lecturer_department: '소속/직함',
          banner_url: null,
          vimeo_id: '1093667661'
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchLectureFaQ = async () => {
      if (!id) return;
      try {
        setFaqError(false);
        const { data } = await apiClient.get<LectureFaqResponse>(
          `/api/lecture-faqs/filtered/${id}?page=0&size=100`
        );
        /// console.log('강의 FAQ API 응답:', {data});
        // /// console.log('강의 FAQ 데이터 필드들:', Object.keys(data.data || {}));
        const items = Array.isArray(data?.data) ? data.data : (data?.data?.content || []);
        if (items.length === 0) {
          const dummy: LectureFaqItem = {
            faq_id: -1,
            question: '이 강의의 자주 묻는 질문은 준비 중입니다. 무엇을 기대할 수 있나요?',
            answer:
              '현재 강의 FAQ는 준비 중입니다. 수강 신청, 수강 기간, 환불 규정, 수강 방법(스트리밍/재생 오류), 과제/자료 제공 여부 등 주요 질문과 답변이 곧 제공될 예정입니다. 궁금한 점은 커뮤니티 Q&A를 이용해 주세요.',
            lecture_id: Number(id),
            lecture_title: ''
          };
          setLectureFaqs([dummy]);
        } else {
          setLectureFaqs(items);
        }
      } catch (error) {
        /// console.error('강의 FAQ 가져오기 오류:', error);
        setFaqError(true);
        setLectureFaqs(null);
      }
    };

    const fetchRecommendedLectures = async () => {
      if (!id) return;
      try {
        setRecommendedError(false);
        // 백엔드 엔드포인트 가정: /api/lectures/recommended/{id}
        // 필요 시 실제 경로에 맞게 변경
        const { data } = await apiClient.get<RecommenderResponse>(
          `/api/lecture-recommendations/${id}/recommendees`
        );
        // 두 응답 형태 모두 지원: data.recommendees 또는 data.content (페이지네이션)
        const paged = (data as any)?.data?.content;
        if (Array.isArray(paged)) {
          if (paged.length === 0) {
            setRecommended([]); // 섹션 생략 조건
            return;
          }
        }
        const items = (Array.isArray(paged) ? paged : (data?.data?.recommendees || [])) as RecommendeeItem[];
        // const title = data?.data?.recommender_title || '추천 강의';
        const title = '추천 강의';
        setRecommendedTitle(title);
        if (items.length === 0) {
          setRecommended([]);
        } else {
          setRecommended(items);
        }
      } catch (error) {
        /// console.error('추천 강의 가져오기 오류:', error);
        setRecommendedError(true);
        setRecommended([]);
      }
    };

    fetchLectureData();
    fetchLectureFaQ();
    fetchRecommendedLectures();
  }, [id]);

  // 시작하기 버튼 클릭 핸들러
  const handleStartLecture = async () => {
    // /// console.log('=== 시작하기 버튼 클릭 ===');
    // /// console.log('isAuthenticated:', isAuthenticated);
    // /// console.log('redux token:', token);
    // /// console.log('localStorage access_token:', localStorage.getItem('access_token'));
    
    if (!id) return;
    
    // 일단 무조건 수강 신청 API 호출해보기
    // /// console.log('로그인 상태 체크 스킵하고 바로 수강 신청 API 호출');
    await handleEnrollment();
  };

  // 수강 신청 API 호출 함수
  const handleEnrollment = async () => {
    // /// console.log('=== 수강 신청 함수 호출 시작 ===');
    
    const API_BASE_URL = process.env.REACT_APP_CAMPUS_API_BASE_URL;
    
    // Redux와 localStorage 둘 다 확인
    const reduxToken = token;
    const localToken = localStorage.getItem('access_token');
    const finalToken = reduxToken || localToken;
    
    // /// console.log('토큰 상태:', {
    //   reduxToken: reduxToken ? 'exists' : 'null',
    //   localToken: localToken ? 'exists' : 'null',
    //   finalToken: finalToken ? 'exists' : 'null',
    //   tokenPreview: finalToken ? finalToken.substring(0, 20) + '...' : 'none'
    // });
    
    if (!finalToken) {
      // /// console.log('토큰이 없습니다. 로그인 모달을 띄웁니다.');
      setShowLoginModal(true);
      return;
    }

    /// console.log('수강 신청 API 요청:', `${API_BASE_URL}/api/enrollments/enroll/${id}`);
    
    try {
      try {
        const { data } = await apiClient.post<{
          code: number; status: string; message: string; data?: any
        }>(
          `/api/enrollments/enroll/${id}`,
          {},
          { withCredentials: true }
        );
        /// console.log('수강 신청 API 응답 object:', {data});

        if (data.code === 200 || data.code === 201 || data.code === 409) {
          // /// console.log('수강 신청 처리 완료. 스트리밍 창을 엽니다.');
          openStreamingWindow();
        } else {
          // /// console.log('수강 신청 실패:', data.message);
          const errorMessage = data.message || '알 수 없는 오류가 발생했습니다';
          alert(`수강 신청에 실패했습니다: ${errorMessage}`);
        }
      } catch (error: any) {
        if (error?.response?.status === 401) {
          // /// console.log('토큰이 만료되었습니다. 인증 상태를 초기화하고 다시 로그인 요청');
          localStorage.removeItem('access_token');
          dispatch(logout());
          setShowLoginModal(true);
          return;
        }
        if (error?.response?.status === 500) {
          const errorMessage = error?.response?.data?.message || error?.response?.data?.error || '서버 오류가 발생했습니다';
          alert(`서버 오류: ${errorMessage}`);
          return;
        }
        /// console.error('수강 신청 API 호출 중 오류:', error);
        alert('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      /// console.error('수강 신청 API 호출 중 오류:', error);
      alert('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  // 스트리밍 창 열기 함수
  const openStreamingWindow = () => {
    if (!id) {
      /// console.error('강의 ID가 없어서 스트리밍 창을 열 수 없습니다.');
      return;
    }
    
    // /// console.log('=== 스트리밍 창 열기 시작 ===');
    // /// console.log('강의 ID:', id);
    // /// console.log('강의 데이터:', lectureData);
    
    // 새 창으로 스트리밍 페이지 띄우기
    const streamingUrl = `/class/streaming/${id}`;
    // /// console.log('스트리밍 URL:', streamingUrl);
    // /// console.log('전체 URL:', window.location.origin + streamingUrl);
    
    const newWindow = window.open(streamingUrl, '_blank', 'width=1440,height=900,scrollbars=yes,resizable=yes');
    
    if (!newWindow) {
      /// console.error('팝업 차단으로 인해 새 창을 열 수 없습니다.');
      alert('팝업 차단을 해제하고 다시 시도해주세요.');
      return;
    }
    
    // /// console.log('새 창이 성공적으로 열렸습니다.');
    
    // 세션 정보 전달 (postMessage 사용)
    if (newWindow && token) {
      // 새 창이 완전히 로드될 때까지 기다린 후 세션 정보 전달
      const sendSessionInfo = () => {
        if (newWindow.closed) return;
        
        try {
          newWindow.postMessage({
            type: 'SESSION_INFO',
            token: token,
            isAuthenticated: isAuthenticated,
            lectureData: lectureData // 강의 데이터도 함께 전달
          }, window.location.origin);
          // /// console.log('세션 정보와 강의 데이터 전달:', {
          //   token: token ? 'exists' : 'null',
          //   lectureData: lectureData
          // });
        } catch (error) {
          /// console.error('Session info transfer failed:', error);
        }
      };
      
      // 새 창 로드 완료 시 세션 정보 전달
      const checkLoaded = setInterval(() => {
        if (newWindow.closed) {
          clearInterval(checkLoaded);
          return;
        }
        
        try {
          if (newWindow.document.readyState === 'complete') {
            clearInterval(checkLoaded);
            setTimeout(sendSessionInfo, 500); // 500ms 후 전달
          }
        } catch (e) {
          // Cross-origin 에러 무시
        }
      }, 100);
      
      // 10초 후 정리
      setTimeout(() => {
        clearInterval(checkLoaded);
      }, 10000);
    }
  };

  // 로그인 성공 후 핸들러
  const handleLoginSuccess = () => {
    // /// console.log('로그인 성공! 모달을 닫습니다.');
    // /// console.log('새로운 토큰 상태:', {
    //   reduxToken: token ? 'exists' : 'null',
    //   localToken: localStorage.getItem('access_token') ? 'exists' : 'null'
    // });
    setShowLoginModal(false);
    
    // 자동으로 수강 신청하지 않음 - 사용자가 다시 "시작하기" 버튼을 눌러야 함
    // /// console.log('로그인 완료. "시작하기" 버튼을 다시 눌러주세요.');
    
    // TODO: 필요하다면 여기서 토스트 메시지나 알림을 표시할 수 있음
  };

  // 로그인 모달 닫기 핸들러
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
  };

  // 초기 탭 위치 저장
  useEffect(() => {
    if (tabSectionRef.current && originalTabPositionRef.current === 0) {
      originalTabPositionRef.current = tabSectionRef.current.offsetTop;
    }
  }, [lectureData]); // lectureData가 로딩된 후에 실행

  // 강의 정보 (임시 데이터 제거)
  const defaultImage = '/images/coffee-chat-default-banner.png';

  // 인디케이터 위치 업데이트
  const updateIndicatorPosition = useCallback((tabId: string) => {
    const activeTabElement = tabRefs.current[tabId];
    const tabListElement = tabListRef.current;
    
    if (activeTabElement && tabListElement) {
      const tabRect = activeTabElement.getBoundingClientRect();
      const listRect = tabListElement.getBoundingClientRect();
      
      const left = tabRect.left - listRect.left;
      const width = tabRect.width;
      
      // 헤더 상태일 때 패딩 영역보다 왼쪽에 있으면 숨김
      let shouldHide = false;
      if (isTabSticky) {
        const containerPadding = window.innerWidth > 1024 ? 80 : window.innerWidth > 768 ? 64 : 24;
        shouldHide = left + width <= containerPadding; // 탭 전체가 패딩 영역에 있으면 숨김
      }
      
      // 헤더 상태에서는 transition 없이 즉시 나타남
      const indicatorElement = document.querySelector('.tab-indicator') as HTMLElement;
      if (indicatorElement && isTabSticky) {
        indicatorElement.style.transition = 'none';
        setTimeout(() => {
          if (indicatorElement) {
            indicatorElement.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          }
        }, 10);
      }
      
      setIndicatorStyle({
        left: shouldHide ? -1000 : left,
        width: shouldHide ? 0 : width
      });
    }
  }, [isTabSticky]);

  // activeTab 변경 시 인디케이터 위치 업데이트
  useEffect(() => {
    updateIndicatorPosition(activeTab);
  }, [activeTab, updateIndicatorPosition, isTabSticky]); // isTabSticky 의존성 추가

  // 초기 렌더링 후 인디케이터 위치 설정
  useEffect(() => {
    const timer = setTimeout(() => {
      updateIndicatorPosition(activeTab);
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTab, updateIndicatorPosition]);

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      if (!tabSectionRef.current || originalTabPositionRef.current === 0) return;

      const scrollY = window.scrollY;
      const headerHeight = 80; // 헤더 높이 정의
      
      // 원래 탭 위치 사용 (변하지 않는 값)
      const tabSectionTop = originalTabPositionRef.current;
      
      // 탭 영역을 완전히 지나친 후에 sticky 활성화
      const tabSectionBottom = tabSectionTop + 56; // 탭 높이 56px
      const shouldBeSticky = scrollY >= tabSectionBottom - headerHeight;
      
      // 디버깅 로그 추가
      // if (scrollY > 0) {
        // /// console.log('Scroll Debug:', {
        //   scrollY,
        //   tabSectionTop,
        //   tabSectionBottom,
        //   shouldBeSticky,
        //   currentIsSticky: isTabSticky
        // });
      // }
      
      if (shouldBeSticky !== isTabSticky) {
        setIsTabSticky(shouldBeSticky);
        /// console.log('Sticky state changed:', shouldBeSticky);
        // sticky 상태 변경 시 인디케이터 위치 업데이트
        setTimeout(() => {
          updateIndicatorPosition(activeTab);
        }, 50);
      }

      // 탭 클릭 중일 때는 스크롤에 의한 activeTab 변경 무시
      if (isTabClicked) {
        // /// console.log('Tab click in progress, ignoring scroll-based tab change');
        return;
      }

      // 목표 스크롤 위치에 근접했으면 스크롤 기반 탭 변경 무시
      if (targetScrollPosition !== null) {
        const scrollTolerance = 50; // 50px 허용 오차
        if (Math.abs(scrollY - targetScrollPosition) < scrollTolerance) {
          // /// console.log('Near target scroll position, ignoring scroll-based tab change');
          return;
        }
      }

      // 현재 보이는 섹션 찾기
      const sectionOffsets = tabs.map(tab => {
        const element = sectionRefs.current[tab.id];
        if (!element) {
          // /// console.warn(`Section element not found for tab: ${tab.id}`);
          return { id: tab.id, offset: 0 };
        }
        return { id: tab.id, offset: element.offsetTop };
      });

      // 현재 스크롤 위치에서 가장 적합한 섹션 찾기
      const offsetForActiveSection = shouldBeSticky ? headerHeight + 56 + 100 : headerHeight + 100; // 스크롤 인식 오프셋 조정
      const targetScrollPos = scrollY + offsetForActiveSection;
      
      let currentSection = sectionOffsets[0];
      for (let i = sectionOffsets.length - 1; i >= 0; i--) {
        if (sectionOffsets[i].offset <= targetScrollPos) {
          currentSection = sectionOffsets[i];
          break;
        }
      }

      if (currentSection.id !== activeTab) {
        // /// console.log('Scroll-based tab change:', activeTab, '->', currentSection.id, 'scrollY:', scrollY, 'targetPos:', targetScrollPos);
        setActiveTab(currentSection.id);
        // 스크롤 시에는 URL 해시를 업데이트하지 않음 (탭 클릭 시에만 업데이트)
      }
    };

    // 초기 실행
    const timer = setTimeout(() => {
      if (originalTabPositionRef.current === 0 && tabSectionRef.current) {
        originalTabPositionRef.current = tabSectionRef.current.offsetTop;
        /// console.log('Tab position re-initialized:', originalTabPositionRef.current);
      }
      handleScroll();
    }, 100);

    // 스로틀링을 위한 requestAnimationFrame 사용
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    return () => {
      window.removeEventListener('scroll', scrollHandler);
      clearTimeout(timer);
    };
  }, [activeTab, tabs, isTabSticky, updateIndicatorPosition, isTabClicked, targetScrollPosition]); // targetScrollPosition 의존성 추가

  // 탭 클릭 시 해당 섹션으로 스크롤
  const handleTabClick = useCallback((tabId: string) => {
    // /// console.log('=== Tab clicked ===', tabId);
    
    const element = sectionRefs.current[tabId];
    if (!element) {
      /// console.warn('Section element not found for tab:', tabId);
      return;
    }

    // 탭 클릭 플래그 설정
    setIsTabClicked(true);
    
    // 먼저 activeTab 상태 업데이트
    setActiveTab(tabId);
    
    // 즉시 인디케이터 위치 업데이트
    setTimeout(() => {
      updateIndicatorPosition(tabId);
    }, 0);

    const headerHeight = 80; // 고정 헤더 높이
    const tabHeight = 56; // 탭 네비게이션 높이
    
    // sticky 상태에 따른 오프셋 계산
    // const offset = isTabSticky ? headerHeight + tabHeight - 20 : headerHeight;
    const offset = headerHeight+70;
    
    const elementRect = element.getBoundingClientRect();
    const elementPosition = window.pageYOffset + elementRect.top - offset;
    
    // 목표 스크롤 위치 저장
    setTargetScrollPosition(elementPosition);
    
    // 임시로 해시 추가 (공유 가능하도록)
    window.history.replaceState(null, '', `#${tabId}`);
    
    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth'
    });
    
    // 스크롤 완료 후 해시 제거 및 플래그 해제
    setTimeout(() => {
      window.history.replaceState(null, '', window.location.pathname);
      setIsTabClicked(false);
      setTargetScrollPosition(null);
      // /// console.log('Tab click scroll completed, flag reset for:', tabId);
    }, 2000);
  }, [isTabSticky, updateIndicatorPosition]);

  // 페이지 로드 시 해시 확인 후 완전 제거
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && tabs.some(tab => tab.id === hash)) {
      // 해당 위치로 이동
      const timer = setTimeout(() => {
        setActiveTab(hash);
        handleTabClick(hash);
        
        // 이동 후 URL에서 해시 완전 제거 (브라우저가 더 이상 참조할 해시 없앰)
        setTimeout(() => {
          window.history.replaceState(null, '', window.location.pathname);
        }, 500);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [tabs, handleTabClick]);

  return (
    <Layout>
      <Container>
        {/* 메인 콘텐츠 컨테이너 */}
        <MainContainer>
          {/* 로딩 상태 - 스켈레톤 UI */}
          {loading ? (
            <>
              {/* 강의 정보 스켈레톤 */}
              <LectureInfoSection>
                {/* 강의 배너 스켈레톤 */}
                <LectureBanner>
                  <LectureBannerSkeleton />
                </LectureBanner>

                {/* 강의 제목과 정보 스켈레톤 */}
                <LectureHeader>
                  <LectureInfo>
                    <LectureTitleSkeleton />
                    <LectureSubtitleSkeleton />
                  </LectureInfo>
                  <StartButtonSkeleton />
                </LectureHeader>
              </LectureInfoSection>

              {/* 탭 네비게이션 스켈레톤 */}
              <TabSectionSkeleton />
            </>
          ) : (
            /* 실제 강의 정보 (로딩 완료 후) */
            lectureData && (
              <>
                {/* 강의 정보 섹션 */}
                <LectureInfoSection>
                  {/* 강의 배너 이미지 */}
                  <LectureBanner>
                    <LectureBannerImage>
                      {!bannerImageLoaded && (
                        <LectureBannerSkeleton />
                      )}
                      <img 
                        src={lectureData.banner_url || lectureData.thumbnail_url || lectureData.image_url || defaultImage} 
                        alt={lectureData.title}
                        style={{ 
                          display: bannerImageLoaded ? 'block' : 'none' 
                        }}
                        onLoad={() => setBannerImageLoaded(true)}
                        onError={(e) => {
                          /// console.log('배너 이미지 로딩 실패:', e.currentTarget.src);
                          // 무한 루프 방지: 핸들러 제거 후 플레이스홀더 적용
                          (e.currentTarget as HTMLImageElement).onerror = null;
                          e.currentTarget.src = defaultImage;
                          setBannerImageLoaded(true);
                        }}
                      />
                    </LectureBannerImage>
                </LectureBanner>

                {/* 강의 제목과 정보 */}
                <LectureHeader>
                  <LectureInfo>
                    <LectureTitle>{lectureData.title}</LectureTitle>
                    <LectureSubtitle>
                      {lectureData.lecturer_name} · {lectureData.lecturer_department}
                    </LectureSubtitle>
                  </LectureInfo>
                  <StartButton onClick={handleStartLecture}>
                    <ButtonText>시작하기</ButtonText>
                  </StartButton>
                </LectureHeader>
              </LectureInfoSection>

              {/* 탭 네비게이션 */}
              <TabSection ref={tabSectionRef} $isSticky={isTabSticky}>
                <TabContainer>
                  <TabList $isSticky={isTabSticky} ref={tabListRef}>
                    {tabs.map((tab) => (
                      <TabItem 
                        key={tab.id}
                        ref={(el) => tabRefs.current[tab.id] = el}
                        active={activeTab === tab.id}
                        onClick={() => handleTabClick(tab.id)}
                      >
                        <TabText active={activeTab === tab.id}>{tab.label}</TabText>
                      </TabItem>
                    ))}
                    <TabIndicator 
                      className="tab-indicator"
                      style={{
                        left: `${indicatorStyle.left}px`,
                        width: `${indicatorStyle.width}px`
                      }}
                    />
                  </TabList>
                  <TabDivider $isSticky={isTabSticky} />
                </TabContainer>
              </TabSection>

              {/* Sticky 상태일 때 콘텐츠 여백 */}
              {isTabSticky && <StickyPlaceholder />}
              
              {/* 콘텐츠 영역 */}
              <ContentSection>
                {tabs.map((tab, index) => (
                  <ContentItem
                    key={tab.id}
                    ref={(el) => sectionRefs.current[tab.id] = el}
                    id={`section-${tab.id}`}
                  >
                    {/* 섹션 제목 임시 숨김 */}
                    <SectionTitle style={{ display: 'none' }}>{tab.label}</SectionTitle>
                    
                    {/* 탭별 콘텐츠 - API 응답 데이터 사용 */}
                    {tab.id === 'introduction' && lectureData.introduction_url && (
                      <FullWidthImageContainer>
                        <img src={lectureData.introduction_url} alt="강의 소개" />
                      </FullWidthImageContainer>
                    )}
                    
                    {tab.id === 'instructor' && lectureData.lecturer_introduction_url && (
                      <FullWidthImageContainer>
                        <img src={lectureData.lecturer_introduction_url} alt="교수 소개" />
                      </FullWidthImageContainer>
                    )}
                    
                    {tab.id === 'reviews' && lectureData.lecture_review_url && (
                      <ContentImageContainer>
                        <img src={lectureData.lecture_review_url} alt="수강 후기" />
                      </ContentImageContainer>
                    )}
                    
                    {tab.id === 'recommended' && (
                      <>
                        <SectionTitle>{recommendedTitle}</SectionTitle>
                        {recommended && recommended.length > 0 ? (
                          <RecommendedGrid>
                          {(recommended || []).slice(0, 4).map((item, idx) => (
                            <RecommendedCard 
                              key={`${item.lecture_info.id}-${idx}`}
                              onClick={() => navigate(`/class/lecture/${item.lecture_info.id}`)}
                            >
                              <RecommendedThumb>
                                <RecommendedImage
                                  src={pickThumbnail(item.lecture_info) || '/images/banner-placeholder.jpg'}
                                  alt={item.lecture_info.title}
                                  onError={(e) => {
                                    const img = e.currentTarget as HTMLImageElement;
                                    if (img.src.includes('/images/banner-placeholder.jpg')) return;
                                    img.onerror = null;
                                    img.src = '/images/banner-placeholder.jpg';
                                  }}
                                />
                              </RecommendedThumb>
                              <RecommendedMeta>
                                <TagContainer>
                                  {item.lecture_info.tags && item.lecture_info.tags.length > 0 && (
                                    item.lecture_info.tags.slice(0, 4).map((tag, tagIdx) => (
                                      <RecommendedTag key={`${tag}-${tagIdx}`}>{tag}</RecommendedTag>
                                    ))
                                  )}
                                </TagContainer>
                                <RecommendedTitle>{item.lecture_info.title || '제목'}</RecommendedTitle>
                                <RecommendedExtraCaption>{item.lecturer_info.name} · {item.lecturer_info.department}</RecommendedExtraCaption>
                              </RecommendedMeta>
                            </RecommendedCard>
                          ))}
                          </RecommendedGrid>
                        ) : (
                          <EmptyStateContainer>
                            <EmptyStateText>
                              현재 추천할 수 있는 강의가 없습니다.<br />
                              더 많은 강의가 준비되면 추천 강의를 제공해드릴 예정입니다.
                            </EmptyStateText>
                          </EmptyStateContainer>
                        )}
                      </>
                    )}
                    
                    {tab.id === 'faq' && (
                      <>
                        {!faqError && lectureFaqs && lectureFaqs.length > 0 ? (
                          <FAQListContainer>
                            <SectionTitle>자주 묻는 질문</SectionTitle>
                            {lectureFaqs.map((item, index) => {
                              const isOpen = openFaqIndex === index;
                              return (
                                <FAQAccordionItem key={item.faq_id} $open={isOpen}>
                                  <FAQAccordionHeader onClick={() => setOpenFaqIndex(isOpen ? null : index)}>
                                    <FAQAccordionTitle>{item.question}</FAQAccordionTitle>
                                    <FAQAccordionIcon $open={isOpen}>
                                      <img src="/images/arrow-left.svg" alt="FAQ 화살표" width="10" height="21" />
                                    </FAQAccordionIcon>
                                  </FAQAccordionHeader>
                                  {isOpen && (
                                    <FAQAccordionContent>
                                      <FAQAnswer>{item.answer}</FAQAnswer>
                                    </FAQAccordionContent>
                                  )}
                                </FAQAccordionItem>
                              );
                            })}
                          </FAQListContainer>
                        ) : (
                          <FAQImageContainer>
                            <img src="/images/faq.svg" alt="FAQ" />
                          </FAQImageContainer>
                        )}
                      </>
                    )}
                  </ContentItem>
                ))}
              </ContentSection>
            </>
          ))}
        </MainContainer>
        
        {/* 로그인 모달 */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={handleLoginModalClose}
          onLoginSuccess={handleLoginSuccess}
        />
      </Container>
    </Layout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
`;

/* 메인 컨테이너 */
const MainContainer = styled.div`
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 24px ${({ theme }) => theme.layout.containerPadding} 128px;
  display: flex;
  flex-direction: column;
  gap: 64px;
  
  @media (max-width: 1024px) {
    padding: 24px ${({ theme }) => theme.layout.containerPaddingTablet} 128px;
  }
  
  @media (max-width: 768px) {
    padding: 24px ${({ theme }) => theme.layout.containerPaddingMobile} 100px;
    gap: 48px;
  }
`;

/* 강의 정보 섹션 */
const LectureInfoSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: -16px; /* MainContainer gap 64px - 원하는 48px = -16px */
  
  @media (max-width: 768px) {
    gap: 24px;
    margin-bottom: 0; /* 모바일에서는 gap이 이미 48px이므로 조정 불필요 */
  }
`;

const LectureBanner = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 3/1; /* 1200:400 비율 */
  
  @media (max-width: 768px) {
    aspect-ratio: 2/1;
  }
`;

const LectureBannerImage = styled.div`
  width: 100%;
  height: 100%;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    user-select: none;
    pointer-events: none;
    -webkit-user-drag: none;
    -webkit-user-select: none;
  }
`;

const LectureHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const LectureInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LectureTitle = styled.h1`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.08px;
  letter-spacing: -0.66px;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const LectureSubtitle = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 26.1px;
  letter-spacing: 0px;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const StartButton = styled.button`
  width: 240px;
  height: 64px;
  padding: 12px 28px;
  background: #448181;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(68, 129, 129, 0.9);
  }
  
  @media (max-width: 768px) {
    align-self: stretch;
    width: 100%;
    height: 56px;
    padding: 12px 24px;
  }
`;

const ButtonText = styled.span`
  color: white;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

/* 탭 섹션 */
const TabSection = styled.section<{ $isSticky?: boolean }>`
  width: 100%;
  height: 56px;
  background: white;
  z-index: 1000;
  transition: all 0.2s ease;
  
  ${props => props.$isSticky && `
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    background: white;
  `}
`;

const TabContainer = styled.div`
  width: 100%;
  height: 56px;
  position: relative;
`;

const TabList = styled.div<{ $isSticky?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 48px;
  overflow-x: auto;
  overflow-y: hidden;
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.layout.containerPadding} 0 ${props => props.$isSticky ? ({ theme }) => theme.layout.containerPadding : '0'};
  height: 56px;
  
  /* 스크롤바 숨기기 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 1024px) {
    padding: 0 ${({ theme }) => theme.layout.containerPaddingTablet} 0 ${props => props.$isSticky ? ({ theme }) => theme.layout.containerPaddingTablet : '0'};
  }
  
  @media (max-width: 768px) {
    gap: 24px;
    padding: 0 ${({ theme }) => theme.layout.containerPaddingMobile} 0 ${props => props.$isSticky ? ({ theme }) => theme.layout.containerPaddingMobile : '0'};
  }
`;

const TabItem = styled.div<{ active?: boolean }>`
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  padding: 0;
  height: 56px;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    height: 56px;
  }
`;

const TabText = styled.span<{ active?: boolean }>`
  color: ${props => props.active ? '#296768' : 'rgba(55, 56, 60, 0.28)'};
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: -0.24px;
  transition: color 0.2s ease;
  
  ${TabItem}:hover & {
    color: #296768;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 22px;
  }
`;

const TabIndicator = styled.div`
  position: absolute;
  bottom: 1px;
  height: 2px;
  background: #296768;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: left;
  z-index: 1;
  
  /* 컨테이너 밖으로 나가면 숨김 */
  overflow: hidden;
`;

const TabDivider = styled.div<{ $isSticky?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
  display: ${props => props.$isSticky ? 'none' : 'block'};
`;

/* Sticky 플레이스홀더 */
const StickyPlaceholder = styled.div`
  height: 56px;
  width: 100%;
`;

/* 콘텐츠 섹션 */
const ContentSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: -64px; /* 탭 아래 여백 제거 */
`;

const ContentItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  
  &:not(:last-child) {
    margin-bottom: 128px; /* 각 섹션 간의 여백 */
  }
  
  @media (max-width: 768px) {
    &:not(:last-child) {
      margin-bottom: 96px; /* 모바일에서는 조금 줄임 */
    }
  }
`;

/* 스켈레톤 UI 컴포넌트들 */
const LectureBannerSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const LectureTitleSkeleton = styled.div`
  width: 60%;
  height: 38px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin-bottom: 4px;
  
  @media (max-width: 768px) {
    height: 32px;
  }
`;

const LectureSubtitleSkeleton = styled.div`
  width: 40%;
  height: 26px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @media (max-width: 768px) {
    height: 24px;
  }
`;

const StartButtonSkeleton = styled.div`
  width: 240px;
  height: 64px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 56px;
  }
`;

const TabSectionSkeleton = styled.div`
  width: 100%;
  height: 56px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
`;

/* 로딩 상태 제거 */
const LoadingContainer = styled.div`
  display: none;
`;

const LoadingText = styled.div`
  display: none;
`;

/* 이미지 컨테이너들 */
const FullWidthImageContainer = styled.div`
  width: 100vw;
  margin-left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  /* overflow: hidden 제거 - 이미지가 컨텐츠 영역을 벗어나도록 */
  
  img {
    width: calc(100vw + 2640px); /* 좌우 1320px씩 더 넓게 */
    height: auto;
    // max-height: 50vh;
    display: block;
    object-fit: cover;
    // min-width: calc(1200px + 2640px); /* 컨텐츠 영역 + 크롭 영역 */
  }
  
  @media (max-width: 1200px) {
    img {
      width: calc(100vw + 2640px);
      // min-width: calc(100vw + 2640px);
    }
  }
  
  @media (max-width: 768px) {
    img {
      width: calc(100vw + 1320px); /* 모바일에서는 좌우 660px씩 */
      // min-width: calc(100vw + 1320px);
    }
  }
  width: 100%;
`;

const ContentImageContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const FAQImageContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  
  img {
    width: calc(100% + 40px); /* 좌우 20px씩 더 넓게 */
    height: auto;
    display: block;
  }
`;

// FAQ 리스트 스타일
const FAQListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FAQAnswer = styled.p`
  margin: 0;
  color: rgba(55, 56, 60, 0.85);
  font-size: 16px;
  line-height: 24px;
`;

const FAQHeaderTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #000000;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 136%;
`;

const FAQAccordionItem = styled.div<{ $open?: boolean }>`
  border-bottom: 1px solid rgba(112, 115, 124, 0.15);
  background: #fff;
  overflow: hidden;
`;

const FAQAccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;
  background: #fff;
  border: none;
  cursor: pointer;
`;

const FAQAccordionTitle = styled.span`
  color: #171719;
  font-size: 18px;
  font-weight: 700;
  text-align: left;
`;

const FAQAccordionIcon = styled.span<{ $open?: boolean }>`
  height: 21px;
  display: inline-flex;
  transform: rotate(${props => (props.$open ? '90deg' : '-90deg')});
  transition: transform 0.2s ease;
  color: rgba(55, 56, 60, 0.5);
`;

const FAQAccordionContent = styled.div`
  padding: 0 24px 20px 24px;
  background: #fff;
`;

// 추천 강의 스타일
const RecommendedGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const RecommendedCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
`;

const RecommendedThumb = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  aspect-ratio: 3/2;
`;

const RecommendedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  transition: transform 0.3s ease;
  
  ${RecommendedCard}:hover & {
    transform: scale(1.05);
  }
`;

const RecommendedMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TagContainer = styled.div`
  align-self: stretch;
  overflow: hidden;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
  display: flex;
  flex-wrap: wrap;
`;

const RecommendedTag = styled.div`
  height: 20px;
  padding-left: 6px;
  padding-right: 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: flex;
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
  word-wrap: break-word;
`;

const RecommendedTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const RecommendedCaption = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  margin: 0;
`;

const RecommendedExtraCaption = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  margin: 0;
`;

// 섹션 제목 통일 스타일 (요청 스펙)
const SectionTitle = styled.h3`
  margin: 0 0 32px 0;
  color: #296768;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  font-size: 28px;
  line-height: 136%;
`;

// 빈 상태 컨테이너
const EmptyStateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  min-height: 200px;
  
  @media (max-width: 768px) {
    padding: 60px 16px;
    min-height: 150px;
  }
`;

const EmptyStateText = styled.p`
  color: rgba(55, 56, 60, 0.6);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

export default ClassLecture;
